import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Link as MuiLink,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  School as SchoolIcon,
  Payments as PaymentsIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { parentService } from '../../services/api';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Parent {
  id: number;
  russian_name: string;
  spanish_name: string;
  email: string;
  phone: string;
  country_code: string;
  is_active: boolean;
  notes: string;
  student: {
    first_name: string;
    last_name: string;
  }
}

interface PaymentStats {
  total_paid: number;
  total_payments: number;
  last_payment: string | null;
  students_count: number;
}

interface Payment {
  id: number;
  student_id: number;
  parent_id: number;
  month: string;
  year: number;
  amount: string;
  payment_date: string;
  status: string;
  student?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ParentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [parent, setParent] = useState<any>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    total_paid: 0,
    total_payments: 0,
    last_payment: null,
    students_count: 0
  });

  useEffect(() => {
    loadParentData();
  }, [id]);

  const loadParentData = async () => {
    try {
      setLoading(true);
      const [parentRes, studentsRes, paymentsRes] = await Promise.all([
        parentService.getById(parseInt(id!)),
        parentService.getStudents(parseInt(id!)),
        parentService.getPaymentHistory(parseInt(id!))
      ]);
      
      setParent(parentRes.data);
      setStudents(studentsRes.data.data || []);
      setPayments(paymentsRes.data.data.data || []);
      setStats({
        total_paid: paymentsRes.data.stats?.total_paid || 0,
        total_payments: paymentsRes.data.stats?.total_payments || 0,
        last_payment: paymentsRes.data.stats?.last_payment || null,
        students_count: paymentsRes.data.stats?.students_count || 0
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsApp = () => {
    if (!parent?.phone) return;
    
    const message = `Hola ${parent.spanish_name || parent.russian_name}, te escribo desde el Sistema Financiero Escuela Origen.`;
    window.open(`https://wa.me/${parent.phone.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSendEmail = () => {
    if (!parent?.email) return;
    
    const subject = 'Información sobre pagos escolares';
    const body = `Estimado/a ${parent.spanish_name || parent.russian_name},\n\n`;
    window.location.href = `mailto:${parent.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const paymentColumns: GridColDef<Payment>[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
        field: 'student',
        headerName: 'Estudiante',
        //width: 200,
        flex: 1.5,
        renderCell: (params) => {
          const student = params.row.student;
          return (
            <Typography fontWeight="medium">
              {student ? `${student.first_name} ${student.last_name}` : 'N/A'}
            </Typography>
          )
        }
    },
    {
      field: 'month_year',
      headerName: 'Período',
      flex: .5,
      renderCell: (params) => (
        <Typography fontWeight="medium">
          {params.row.month}/{params.row.year}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: .5,
      renderCell: (params) => (
        <Typography fontWeight="medium">
          ₽ {params.row.amount.toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payment_date 
            ? dayjs(params.row.payment_date).format('DD/MM/YYYY')
            : '-'
          }
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'paid' ? 'Pagado' : 
                 params.row.status === 'pending' ? 'Pendiente' : params.row.status}
          color={params.row.status === 'paid' ? 'success' : 
                 params.row.status === 'pending' ? 'warning' : 'default'}
          size="small"
        />
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !parent) {
    return (
      <Alert severity="error">
        {error || 'Padre no encontrado'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton component={Link} to="/parents" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {parent.spanish_name || parent.russian_name}
            </Typography>
            {parent.spanish_name && parent.russian_name && (
              <Typography variant="subtitle1" color="text.secondary">
                {parent.russian_name}
              </Typography>
            )}
          </Box>
          <Chip
            icon={parent.is_active ? <CheckCircleIcon /> : <WarningIcon />}
            label={parent.is_active ? 'Activo' : 'Inactivo'}
            color={parent.is_active ? 'success' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box>
          <Tooltip title="Enviar WhatsApp">
            <IconButton 
              onClick={handleSendWhatsApp}
              disabled={!parent.phone}
              sx={{ mr: 1 }}
              color="success"
            >
              <WhatsAppIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar Email">
            <IconButton 
              onClick={handleSendEmail}
              disabled={!parent.email}
              sx={{ mr: 1 }}
              color="primary"
            >
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/parents/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Información General" />
          <Tab label="Estudiantes" />
          <Tab label="Historial de Pagos" />
          <Tab label="Estadísticas" />
        </Tabs>
      </Paper>

      {/* Tab: Información General */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de Contacto
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ width: '30%', bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Email</Typography>
                        </TableCell>
                        <TableCell>
                          {parent.email ? (
                            <MuiLink href={`mailto:${parent.email}`}>
                              {parent.email}
                            </MuiLink>
                          ) : (
                            'No especificado'
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Teléfono</Typography>
                        </TableCell>
                        <TableCell>
                          {parent.phone ? (
                            <MuiLink href={`tel:${parent.country_code}${parent.phone}`}>
                              {parent.country_code} {parent.phone}
                            </MuiLink>
                          ) : (
                            'No especificado'
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">País</Typography>
                        </TableCell>
                        <TableCell>
                          {parent.country_code === '+7' && '🇷🇺 Rusia'}
                          {parent.country_code === '+53' && '🇨🇺 Cuba'}
                          {parent.country_code === '+1' && '🇺🇸 USA/Canadá'}
                          {parent.country_code === '+34' && '🇪🇸 España'}
                          {![ '+7', '+53', '+1', '+34'].includes(parent.country_code) && parent.country_code}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estadísticas Rápidas
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <SchoolIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {students.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Estudiantes
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <PaymentsIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" color="success.main">
                        ₽{payments
                          .filter((p: any) => p.status === 'paid')
                          .reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0)
                          .toLocaleString()
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Pagado
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <HistoryIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {payments.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pagos Realizados
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <PersonIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4">
                        {new Set(payments.map((p: any) => p.student?.id)).size}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Estudiantes Distintos
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {parent.notes && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notas
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">
                      {parent.notes}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab: Estudiantes */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2}>
          {students.length > 0 ? (
            students.map((student) => (
              <Grid size={{ xs: 12, md: 4, sm: 6 }} key={student.id}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 }
                  }}
                  onClick={() => navigate(`/students/${student.id}`)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <SchoolIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1">
                          {student.first_name} {student.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Grado: {student.grade}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Chip
                        label={student.status === 'active' ? 'Activo' : student.status}
                        color={student.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {student.sponsor ? `Patrocinador: ${student.sponsor}` : ''}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Este padre no tiene estudiantes asignados
              </Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab: Historial de Pagos */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={payments}
            columns={paymentColumns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 }
              }
            }}
            onRowClick={(params) => navigate(`/monthly-payments/${params.id}`)}
            localeText={{
              noRowsLabel: 'No hay pagos registrados'
            }}
          />
        </Paper>
      </TabPanel>

      {/* Tab: Estadísticas */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pagos por Año
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Año</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from(new Set(payments.map((p: any) => p.year))).map((year) => {
                        const yearPayments = payments.filter((p: any) => p.year === year);
                        const total = yearPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
                        return (
                          <TableRow key={year as number}>
                            <TableCell>{year}</TableCell>
                            <TableCell align="right">{yearPayments.length}</TableCell>
                            <TableCell align="right">₽{total.toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pagos por Estudiante
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Estudiante</TableCell>
                        <TableCell align="right">Pagos</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((student) => {
                        const studentPayments = payments.filter(
                          (p: any) => p.student?.id === student.id
                        );
                        const total = studentPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
                        return (
                          <TableRow key={student.id}>
                            <TableCell>
                              {student.first_name} {student.last_name}
                            </TableCell>
                            <TableCell align="right">{studentPayments.length}</TableCell>
                            <TableCell align="right">₽{total.toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default ParentDetail;